import { ArrowRight, CalendarDays, Check, Home, FlaskConical } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { HERO_IMAGE, THC } from "@/lib/tankHealthCheck";

export function HealthCheckHero({ address, onAddressChange, onEligibility, onBook, onAutocompleteAvailability }) {
  return (
    <section className="relative overflow-hidden" data-testid="thc-hero"
      style={{ background: "linear-gradient(105deg,#FFFFFF 0%,#F4FAFD 42%,#EAF7FD 100%)" }}>
      <div className="mx-auto max-w-[1440px] px-[18px] md:px-7 xl:px-10">
        <div className="lg:grid lg:grid-cols-[58%_42%] lg:min-h-[510px] lg:items-center">
          <div className="pt-8 pb-7 lg:py-12 lg:pr-10 relative z-10">
            <h1 className="font-extrabold tracking-[-0.02em] leading-[1.0] text-[38px] sm:text-[46px] lg:text-[52px] xl:text-[58px]"
              style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
              WHAT’S REALLY GOING ON
              <span className="block" style={{ color: THC.bright }}>IN YOUR TANK WATER?</span>
            </h1>
            <p className="mt-3 font-bold text-[22px] sm:text-[25px] xl:text-[29px] leading-[1.15]"
              style={{ color: THC.navy }}>
              Book a Complimentary Tank Water Health Check
            </p>
            <p className="mt-2.5 max-w-[540px] text-[16px] xl:text-[20px] leading-[1.45]"
              style={{ color: THC.body }}>
              If your home relies on tank water, clear-looking water does not always tell the full story.
            </p>

            <div className="mt-6 w-full lg:max-w-[600px] bg-white rounded-[12px] p-[18px] xl:p-5 flex items-center gap-4"
              style={{ boxShadow: THC.shadow }} data-testid="thc-address-card">
              <span className="shrink-0 w-[46px] h-[46px] rounded-[10px] grid place-items-center"
                style={{ backgroundColor: THC.light }}>
                <Home className="w-[26px] h-[26px]" style={{ color: THC.blue }} aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <label htmlFor="thc-address" className="block text-[13px] font-bold uppercase tracking-wide"
                  style={{ color: THC.navy }}>
                  PROPERTY ADDRESS
                </label>
                {/* Sep 2026 — Google Places (AU only, biased to the Sunshine
                    Coast). This field is the MASTER address for the whole
                    booking; nothing below asks for it again. */}
                <AddressAutocomplete
                  value={address.property_address}
                  onChange={(v) => onAddressChange({
                    property_address: v, address_verified: false,
                    address_source: "typed",
                  })}
                  onPlace={(place) => onAddressChange(place)}
                  biasCircle={{ lat: -26.3868, lng: 153.0928, radius: 45000 }}
                  onAvailability={onAutocompleteAvailability}
                  placeholder="Enter your property address"
                  testId="thc-address-input"
                  className="mt-1 w-full h-[46px] rounded-[8px] border px-3 text-[16px] bg-[#F4FAFD] focus-visible:ring-2 focus-visible:ring-[#09A9E6]" />
                {address.property_address && !address.address_verified
                  && address.autocomplete_live !== false && !address.region_decided ? (
                  <p className="mt-1 text-[12px] font-semibold" style={{ color: THC.red }}
                    data-testid="thc-address-unverified" role="status">
                    Please select your property address from the suggestions.
                  </p>
                ) : address.address_verified ? (
                  <p className="mt-1 text-[12px] font-semibold inline-flex items-center gap-1"
                    style={{ color: THC.blue }} data-testid="thc-address-verified">
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                    {address.suburb ? `${address.suburb} ${address.state} ${address.postcode}` : "Address confirmed"}
                    {address.address_source === "campaign_merge" ? " — from your postcard, tap to change" : ""}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-5 w-full lg:max-w-[600px] rounded-[12px] border p-4 md:p-[18px] flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
              style={{ borderColor: THC.blue, backgroundColor: "#F4FAFD" }} data-testid="thc-water-analysis-callout">
              <span className="shrink-0 w-[42px] h-[42px] rounded-[10px] grid place-items-center mx-auto sm:mx-0"
                style={{ backgroundColor: "#FFFFFF" }}>
                <FlaskConical className="w-[24px] h-[24px]" style={{ color: THC.blue }} aria-hidden="true" />
              </span>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-[14px] md:text-[15px] leading-[1.45] font-semibold" style={{ color: THC.navy }}>
                  Want a deeper water analysis? A discounted pre-purchased Water Analysis Test is
                  available below for customers who want more detailed results.
                </p>
                <button
                  onClick={() => document.getElementById("water-analysis-test")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  data-testid="thc-water-analysis-cta"
                  className="mt-2.5 inline-flex items-center gap-1.5 text-[14px] font-bold underline-offset-2 hover:underline transition-colors"
                  style={{ color: THC.blue }}>
                  See discounted test options below <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-[12px] sm:gap-[15px]">
              <button onClick={onEligibility} data-testid="thc-hero-eligibility"
                className="h-[54px] sm:min-w-[236px] rounded-[8px] text-white text-[16px] xl:text-[17px] font-bold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: THC.blue, boxShadow: THC.shadow }}>
                CHECK MY ELIGIBILITY <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
              <button onClick={onBook} data-testid="thc-hero-book"
                className="h-[54px] sm:min-w-[236px] rounded-[8px] bg-white text-[16px] xl:text-[17px] font-bold inline-flex items-center justify-center gap-2 border-2 transition-transform hover:-translate-y-0.5"
                style={{ borderColor: THC.blue, color: THC.blue }}>
                <CalendarDays className="w-4 h-4" aria-hidden="true" /> BOOK MY HEALTH CHECK
              </button>
            </div>
          </div>

          <div className="relative h-[260px] sm:h-[340px] lg:h-full lg:min-h-[510px]">
            <img src={HERO_IMAGE} alt="Modern Sunshine Coast home with a corrugated rainwater tank"
              className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
            <div className="absolute inset-0 hidden lg:block"
              style={{ background: "linear-gradient(90deg,#EAF7FD 0%,rgba(234,247,253,0.65) 18%,rgba(234,247,253,0) 46%)" }} />
            <p className="absolute top-3 left-3 lg:top-8 lg:left-4 max-w-[260px] text-[21px] lg:text-[26px] leading-[1.15] font-semibold rounded-[10px] px-2 py-1 bg-white/60 lg:bg-transparent"
              style={{ color: THC.navy, fontFamily: "'Caveat',cursive", textShadow: "0 1px 10px rgba(255,255,255,0.75)" }}>
              Cleaner Water<br />Healthier Homes<br />Brighter Futures
            </p>
            <p className="absolute bottom-4 right-4 text-[20px] lg:text-[24px] font-semibold px-3 py-1 rounded-[10px]"
              style={{ color: "#FFFFFF", backgroundColor: "rgba(7,58,122,0.72)", fontFamily: "'Caveat',cursive" }}>
              Proudly Sunshine Coast
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
